import type { DeskIconSpec, Project } from './types'

export const ICONS = {
    myComputer: '/desk-95/icons/my-computer.png',
    notepad: '/desk-95/icons/notepad.png',
    inbox: '/desk-95/icons/inbox.png',
    folder: '/desk-95/icons/folder-closed.png',
    photoshop: '/desk-95/icons/photoshop.webp',
    paint: '/desk-95/icons/mspaint.png',
    calculator: '/desk-95/icons/calculator.png',
    help: '/desk-95/icons/help.png',
    ie: '/desk-95/icons/ie.png'
} as const

export const PROJECTS: Project[] = [
    {
        id: 'noir-leather',
        file: 'NOIR_LEATHER.PSD',
        title: 'Noir Leather',
        headline: 'Noir Leather Archive',
        image: '/projects/noir-leather.jpg',
        alt: 'High-contrast black leather bomber jacket photographed flat on white',
        positioning: 'An outerwear archive reduced to pure silhouette.',
        client: 'Noir Atelier',
        year: '1995',
        discipline: 'Art direction',
        challenge: 'Show a whole leather capsule without letting texture do the selling.',
        direction: 'Flat lay, edge-first lighting, a hard two-tone threshold.',
        outcome: 'The catalogue reads clean at thumbnail size and at full page.',
        intro: 'Art direction for an outerwear archive shot flat and pushed to two tones.',
        description: 'The full AW capsule was shot flat and pushed to two tones, so the jacket reads as a cut-out rather than a product. Every seam had to survive the threshold, which meant lighting each piece for edge first and texture second. The result is a catalogue you can read at thumbnail size.',
        note: 'The threshold was set once and never adjusted per garment, so the run holds together as a set rather than a stack of separate retouches.'
    },
    {
        id: 'lume-eyewear',
        file: 'LUME.PSD',
        title: 'Lume Eyewear',
        headline: 'Lume Eyewear Series',
        image: '/projects/lume-eyewear.jpg',
        alt: 'Close crop of olive acetate spectacle frames on a warm grey backdrop',
        positioning: 'Acetate photographed like sculpture, not accessory.',
        client: 'Lume Optical',
        year: '1996',
        discipline: 'Product photography',
        challenge: 'Make six near-identical frames each worth looking at on their own.',
        direction: 'Macro distance, one light, olive acetate carrying the palette.',
        outcome: 'The campaign ran with no copy beyond the frame name.',
        intro: 'Product photography for an optical house that wanted frames treated as objects.',
        description: 'Six frames, one lens, one light. Shooting at macro distance turned the temple hinge into architecture and let the olive acetate carry the entire palette. The campaign ran with no copy at all beyond the frame name.',
        note: 'Every frame sits on the same axis, so the series can be flipped through like plates and the hinge does all of the storytelling.'
    },
    {
        id: 'bloom-sneaker',
        file: 'BLOOM.PSD',
        title: 'Bloom Sneaker',
        headline: 'Bloom Sneaker Campaign',
        image: '/projects/bloom-sneaker.jpg',
        alt: 'Low-angle shot of a cream sneaker stepping over a field of orange and pink cosmos flowers',
        positioning: 'A launch shot from ground level, inside the meadow.',
        client: 'Bloom Footwear',
        year: '1997',
        discipline: 'Campaign',
        challenge: 'Communicate sustainability without the usual recycled-bottle clichés.',
        direction: "Real meadows, worm's-eye angles, chalky-cream type.",
        outcome: 'Pre-orders cleared 60% of the season in week one.',
        intro: 'Campaign art direction for the spring drop of a low-impact sneaker line.',
        description: 'The brief asked for a hero image with no studio in it. We buried the camera in a cosmos field and shot upward on a fisheye, so the outsole becomes the horizon and the sky does the colour grading. Shot across two mornings before the wind picked up.',
        note: 'Because the sky did the grading, nothing was pushed in post beyond a single contact-sheet contrast pass across the whole take.'
    },
    {
        id: 'saffron-issue',
        file: 'SAFFRON.PSD',
        title: 'Saffron Issue',
        headline: 'Saffron Issue',
        image: '/projects/saffron-issue.jpg',
        alt: 'Model in a hand-printed yellow coat and matching headwrap against a saffron background',
        positioning: 'One colour, top to bottom, print and set alike.',
        client: 'Saffron Quarterly',
        year: '1996',
        discipline: 'Editorial',
        challenge: 'Keep a head-to-toe monochrome issue readable page after page.',
        direction: 'Ink prints screened at garment scale, set painted to match.',
        outcome: 'The issue sold through its print run before the second dispatch.',
        intro: 'An editorial issue built end to end around a single dyed colour.',
        description: 'A monochrome issue built around a single dyed backdrop. The prints were drawn in ink, blown up to garment scale and screened onto the cloth, then the set was painted to match within a shade. Styling had to fight the background to stay readable, which is where the tortoiseshell came in.',
        note: 'Tortoiseshell was the only material allowed to break the dye, and it is what keeps the styling from disappearing into the backdrop.'
    },
    {
        id: 'pulp-press',
        file: 'PULP.PSD',
        title: 'Pulp Press',
        headline: 'Pulp Press Identity',
        image: '/projects/pulp-press.jpg',
        alt: 'Extreme macro of a cut orange showing the segments and pith',
        positioning: 'Identity for a juice press, built from a single cross-section.',
        client: 'Pulp Press',
        year: '1998',
        discipline: 'Identity',
        challenge: 'Build an entire system from one photograph, with no illustration.',
        direction: 'Segment radials set the spacing, pith white sets the stock.',
        outcome: 'Rolled out across packaging, signage and print in six weeks.',
        intro: 'A full identity for a cold-press juice bar drawn out of one photograph.',
        description: 'The whole system comes out of one photograph. The segment radials set the logotype spacing, the pith white became the packaging stock, and the flesh gave the only accent in the palette. Labels were printed on uncoated board so the colour sat flat.',
        note: 'Uncoated board keeps the flesh colour matte, so the only gloss anywhere in the system belongs to the fruit itself.'
    },
    {
        id: 'kew-botanical',
        file: 'KEW.PSD',
        title: 'Kew Botanical',
        headline: 'Kew Botanical Plates',
        image: '/projects/kew-botanical.jpg',
        alt: 'Yellow tulip covered in water droplets against a pale background',
        positioning: 'A specimen series shot with glasshouse discipline.',
        client: 'Kew Botanical Society',
        year: '1997',
        discipline: 'Print',
        challenge: 'Bind twelve separate shoots into one continuous plate run.',
        direction: 'Same distance, same height, same hour of the morning.',
        outcome: 'All twelve plates bound as a set with no reshoots.',
        intro: 'A print series of twelve specimens shot to a single glasshouse rule.',
        description: 'Twelve specimens, each photographed at the same distance, same height and same hour of the morning so the plates could be bound as a set. Droplets were misted by hand between frames. The plates run full bleed with the caption set in the gutter.',
        note: 'Captions live in the gutter rather than under the image, which is what lets every plate run full bleed without a caption line breaking the edge.'
    },
    {
        id: 'quiet-quarterly',
        file: 'QUIET_Q4.PSD',
        title: 'Quiet Quarterly',
        headline: 'Quiet Quarterly',
        image: '/projects/quiet-quarterly.jpg',
        alt: 'Black and white portrait blurred by motion, subject turning away from the camera',
        positioning: 'A journal that treats motion blur as the point.',
        client: 'Quiet Quarterly',
        year: '1995',
        discipline: 'Editorial',
        challenge: 'Make a half-missed, unrepeatable portrait feel deliberate.',
        direction: 'A quarter of a second, no flash, one frame per sitter.',
        outcome: 'Four issues shipped on the same grid without a reshoot.',
        intro: 'An editorial system for a portrait journal that treats blur as the subject.',
        description: 'Every portrait in the run was made at a quarter of a second with no flash, so the subject decides how much of themselves is recorded. Nothing was staged twice. The grid gives each frame a full page and a name, and nothing else.',
        note: 'Because nothing is staged twice, the grid stays severe on purpose: a full page, a name, and no caption to explain the frame away.'
    },
    {
        id: 'solenne-womenswear',
        file: 'SOLENNE.PSD',
        title: 'Solenne Womenswear',
        headline: 'Solenne Lookbook',
        image: '/projects/solenne-womenswear.jpg',
        alt: 'Model seated on a clear acrylic stool in a butter-yellow coat and matching trousers',
        positioning: 'Tailoring in one tone, on furniture you can see through.',
        client: 'Solenne',
        year: '1998',
        discipline: 'Lookbook',
        challenge: 'Sell cut and drape when colour is doing none of the work.',
        direction: 'One butter yellow, clear props, unbroken trouser lines.',
        outcome: 'The lookbook carried the season with no additional stills.',
        intro: 'A tailoring lookbook held to one tone across wool, twill and canvas.',
        description: 'The lookbook holds a single colour across wool, twill and canvas, which puts the whole burden on cut and drape. Clear acrylic props keep the seat out of the picture so the trouser line stays unbroken from hip to hem.',
        note: 'Seating had to vanish from the frame, so every prop was cut from clear acrylic and lit from behind the subject to kill its edges.'
    },
    {
        id: 'kestrel-outerwear',
        file: 'KESTREL_FW.PSD',
        title: 'Kestrel Outerwear',
        headline: 'Kestrel Outerwear Campaign',
        image: '/projects/kestrel-outerwear.jpg',
        alt: 'Model in a grey technical coat shot from below through a lattice with an orange flare across the frame',
        positioning: 'Technical outerwear shot through the architecture.',
        client: 'Kestrel',
        year: '1999',
        discipline: 'Campaign',
        challenge: 'Give performance fabric a mood without staging fake weather.',
        direction: 'Midday from below, orange gel dragged through the foreground.',
        outcome: 'The flare stayed in the plate and became the season signature.',
        intro: 'A technical outerwear campaign shot from the floor of a lattice atrium.',
        description: 'Shot from the floor of a lattice atrium at midday, with a sheet of orange gel dragged through the foreground on every other frame. The flare was left in the plate rather than added later, so the colour behaves like weather.',
        note: 'Every other frame was shot clean, which gave the edit a rhythm between the gelled plates and the plain ones without any work in post.'
    },
    {
        id: 'poppy-press',
        file: 'POPPY.PSD',
        title: 'Poppy Press',
        headline: 'Poppy Press Covers',
        image: '/projects/poppy-press.jpg',
        alt: 'Soft-focus orange poppy petal against a pale yellow background',
        positioning: 'Covers for a small press, one flower each.',
        client: 'Poppy Press',
        year: '1999',
        discipline: 'Book design',
        challenge: 'Make nine covers read as a series without repeating an image.',
        direction: 'One flower, out of focus, on its own tint, every time.',
        outcome: 'Nine titles shipped in the first year on a single template.',
        intro: 'Cover design for a poetry imprint working to one rule per title.',
        description: 'A poetry imprint with one rule: every cover is a single flower, out of focus, on its own tint. Titles are foil-blocked small in the lower left so the image is never crowded. Nine titles shipped in the first year.',
        note: 'Titles are foil-blocked small in the lower left, which keeps the image uncrowded and still lines the spines up on a shelf.'
    }
]

export const projectById = (id: string): Project | undefined => PROJECTS.find(project => project.id === id)

/**
 * The six thumbnails shown in a project sheet: the document itself first, then
 * the neighbouring documents in archive order.
 */
export const galleryFor = (project: Project, count = 6): Project[] => {
    const start = PROJECTS.findIndex(item => item.id === project.id)

    return Array.from({ length: Math.min(count, PROJECTS.length) }, (_, index) => (
        PROJECTS[(start + index) % PROJECTS.length]
    ))
}

/**
 * Reading order for the desktop grid: three project columns filled row by row
 * down the left of the wallpaper.
 */
const DESKTOP_ORDER = [
    'noir-leather',
    'solenne-womenswear',
    'poppy-press',
    'kestrel-outerwear',
    'quiet-quarterly',
    'saffron-issue',
    'lume-eyewear',
    'bloom-sneaker',
    'pulp-press',
    'kew-botanical'
]

const PROJECT_COLUMNS = [3, 13, 23]
const PROJECT_ROWS = [5, 25, 45, 64]

/**
 * Percentage positions seeded into icon state on mount: three project columns
 * on the left, system icons down the right edge, Photoshop one column in.
 */
export const ICON_LAYOUT: DeskIconSpec[] = [
    ...DESKTOP_ORDER.flatMap((id, index) => {
        const project = projectById(id)

        if (!project) return []

        return [{
            uid: `icon:${project.id}`,
            label: project.file,
            kind: 'project' as const,
            projectId: project.id,
            xPct: PROJECT_COLUMNS[index % 3],
            yPct: PROJECT_ROWS[Math.floor(index / 3)]
        }]
    }),
    {
        uid: 'icon:photoshop',
        label: 'Adobe Photoshop',
        kind: 'app',
        icon: ICONS.photoshop,
        xPct: 84,
        yPct: 5
    },
    {
        uid: 'icon:my-computer',
        label: 'My Computer',
        kind: 'app',
        icon: ICONS.myComputer,
        xPct: 92,
        yPct: 5
    },
    {
        uid: 'icon:notepad',
        label: 'About Me',
        kind: 'app',
        icon: ICONS.notepad,
        xPct: 92,
        yPct: 23
    },
    {
        uid: 'icon:inbox',
        label: 'Inbox',
        kind: 'app',
        icon: ICONS.inbox,
        xPct: 92,
        yPct: 41
    }
]
